import { useState, useEffect } from 'react';

interface HeroSectionProps {
  onCTAClick: () => void;
}

const trustBadges = ['地域密着', '最短対応', '無料相談', 'PC無料引き取り'];

const HeroSection: React.FC<HeroSectionProps> = ({ onCTAClick }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex flex-col">
      {/* Navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img
            src="https://static.readdy.ai/image/50d82ed5e67db213de3cd7b55d8920db/7c6a3ed7e0c4b6c2e2894c907f853820.png"
            alt="会社ロゴ"
            className="h-10 w-auto"
          />
          <button
            onClick={onCTAClick}
            className={`whitespace-nowrap text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer ${
              scrolled
                ? 'bg-sky-600 text-white hover:bg-sky-700'
                : 'bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30'
            }`}
          >
            無料診断を申し込む
          </button>
        </div>
      </header>

      {/* Hero background */}
      <div className="relative flex-1 flex items-center justify-center min-h-screen">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/50336c7ac461c8d315e4ecc2d11ae294.jpg"
            alt="セキュリティイメージ"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/75 via-slate-900/60 to-slate-900/80" />
        </div>

        <div className="relative z-10 text-center px-6 py-32 w-full max-w-4xl mx-auto">

          {/* Alert label – 強調版 */}
          <div className="inline-flex items-center gap-4 mb-8 bg-red-500/20 border border-red-400/50 rounded-full px-6 py-2.5 backdrop-blur-sm">
            <span className="block w-2 h-2 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
            <span className="text-red-300 text-sm font-bold tracking-widest uppercase">
              サイバー攻撃&nbsp;&nbsp;増加中
            </span>
            <span className="block w-2 h-2 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
          </div>

          {/* Main heading */}
          <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 tracking-tight">
            神奈川県の中小企業様へ
            <span className="block mt-3 text-sky-300">
              そのネット環境、<br className="md:hidden" />
              本当に守られていますか？
            </span>
          </h1>

          {/* Dot divider */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="block w-20 h-px bg-white/20" />
            <span className="block w-2 h-2 rounded-full bg-sky-400" />
            <span className="block w-20 h-px bg-white/20" />
          </div>

          {/* Subheadline */}
          <p className="text-slate-200 text-lg md:text-xl mb-10 leading-relaxed max-w-2xl mx-auto">
            神奈川県でセキュリティ相談が増えています
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14">
            <button
              onClick={onCTAClick}
              className="whitespace-nowrap bg-sky-500 hover:bg-sky-400 text-white font-bold text-lg px-10 py-4 rounded-full transition-all duration-200 cursor-pointer shadow-lg shadow-sky-500/30 w-full sm:w-auto"
            >
              無料セキュリティ診断はこちら
            </button>
            <button
              onClick={onCTAClick}
              className="whitespace-nowrap bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold text-lg px-10 py-4 rounded-full border border-white/30 transition-all duration-200 cursor-pointer w-full sm:w-auto"
            >
              お気軽にご相談ください
            </button>
          </div>

          {/* Trust badges – 強調版 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
            {trustBadges.map((label) => (
              <div
                key={label}
                className="bg-white/10 backdrop-blur-sm border border-white/25 rounded-xl py-4 px-3 flex flex-col items-center justify-center gap-1.5"
              >
                <span className="block w-5 h-0.5 bg-sky-400 rounded-full" />
                <span className="text-white text-sm font-bold text-center leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator – CSS chevron, no icon */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/50">
          <span className="text-xs tracking-widest">SCROLL</span>
          <span className="block w-4 h-4 border-r-2 border-b-2 border-white/40 rotate-45 animate-bounce mt-1" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
